// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import {
  ChartData,
  TooltipData,
  createDataDatumAttribute,
  createDirectFlexibleTooltipMouseMoveHandler,
  createFlexibleTooltipMouseMoveHandler,
  formatMinutesToFractionalHours,
} from '@/costs/components/chart-tooltip-helpers';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrency, formatNumber } from '@/utilities/number';
import { Box, Typography, useTheme } from '@mui/material';
import { AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Text } from '@visx/text';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useEffect, useRef, useState } from 'react';

interface HorizontalBarChartProps {
  data: ChartData[];
  loading?: boolean;
  displayMode?: 'currency' | 'time';
  /** Currency the values are denominated in (currency mode only). */
  currencyCode?: IsoCurrencyCode;
}

const MARGIN = { top: 20, right: 100, bottom: 30, left: 165 };
const TICK_LENGTH = 5;

const formatCurrencyAbbreviated = (
  value: number,
  currencyCode?: IsoCurrencyCode
): string => {
  return formatCurrency(value, {
    abbreviate: true,
    digits: 1,
    hideZeroDecimals: true,
    currencyCode,
  });
};

const formatCurrencyAxis = (
  value: number,
  currencyCode?: IsoCurrencyCode
): string => {
  return formatCurrency(value, {
    abbreviate: true,
    digits: 1,
    hideZeroDecimals: true,
    currencyCode,
  });
};

const formatTimeAbbreviated = (minutes: number): string => {
  return formatMinutesToFractionalHours(minutes);
};

const formatTimeAxis = (minutes: number): string => {
  return formatMinutesToFractionalHours(minutes);
};

export function HorizontalBarChart(props: HorizontalBarChartProps) {
  const {
    data,
    loading = false,
    displayMode = 'currency',
    currencyCode,
  } = props;
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const containerRefCallback = (node: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    if (node) {
      const updateWidth = () => {
        const width = node.getBoundingClientRect().width;
        if (width > 0) {
          setContainerWidth(Math.max(500, width));
        }
      };

      // Update width immediately
      updateWidth();

      // Set up ResizeObserver
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(updateWidth);
      });
      resizeObserverRef.current.observe(node);
    }
  };

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  if (loading) {
    return (
      <Typography color="text.secondary" textAlign="center" py={4}>
        Loading...
      </Typography>
    );
  }

  if (data.length === 0) {
    return (
      <EmptyState
        title="No data available"
        message="Try adjusting your filters or date range to see data"
      />
    );
  }

  // Calculate height with maximum of 8 bars visible
  const maxBars = 8;
  const barHeight = 40;
  const maxHeight = maxBars * barHeight + MARGIN.top + MARGIN.bottom;
  const naturalHeight = data.length * barHeight + MARGIN.top + MARGIN.bottom;
  const height = Math.max(300, Math.min(maxHeight, naturalHeight));
  const shouldScroll = data.length > maxBars;

  return (
    <Box
      ref={containerRefCallback}
      sx={{
        width: '100%',
        height: shouldScroll ? height : 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {shouldScroll ? (
        <>
          {/* Scrollable chart area */}
          <Box
            ref={scrollableRef}
            sx={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            <SVGChart
              data={data}
              width={containerWidth}
              height={naturalHeight}
              showXAxis={false}
              displayMode={displayMode}
              currencyCode={currencyCode}
            />
          </Box>
          {/* Static X-axis */}
          <Box sx={{ flexShrink: 0 }}>
            <svg
              width={containerWidth}
              height={MARGIN.bottom}
              style={{ display: 'block' }}
            >
              <StaticXAxis
                data={data}
                width={containerWidth}
                currencyCode={currencyCode}
              />
            </svg>
          </Box>
        </>
      ) : (
        <SVGChart
          data={data}
          width={containerWidth}
          height={height}
          showXAxis={true}
          displayMode={displayMode}
          currencyCode={currencyCode}
        />
      )}
    </Box>
  );
}

interface SVGChartProps {
  data: ChartData[];
  width: number;
  height: number;
  showXAxis?: boolean;
  displayMode: 'currency' | 'time';
  currencyCode?: IsoCurrencyCode;
}

function SVGChart(props: SVGChartProps) {
  const {
    data,
    width,
    height,
    showXAxis = true,
    displayMode,
    currencyCode,
  } = props;
  const theme = useTheme();

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<TooltipData>();

  const { containerRef: tooltipContainerRef, TooltipInPortal } =
    useTooltipInPortal({
      detectBounds: true,
      scroll: true,
    });

  const handleMouseMove = createFlexibleTooltipMouseMoveHandler(
    showTooltip,
    displayMode,
    currencyCode
  );

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  // Calculate appropriate upper bound for the domain using "nice" intervals
  const maxValue = Math.max(...data.map((d) => d.value), 0);
  const getNiceUpperBound = (value: number): number => {
    if (value === 0) return 100;

    // Find the NEXT power of 10 above the value, then go one level down
    const nextPowerOfTen = Math.pow(10, Math.ceil(Math.log10(value)));
    const incrementPowerOfTen = nextPowerOfTen / 10;

    // Round up to the next increment of that power of 10
    const result = Math.ceil(value / incrementPowerOfTen) * incrementPowerOfTen;
    return result;
  };

  // Scales
  const xScale = scaleLinear({
    domain: [0, getNiceUpperBound(maxValue)],
    range: [0, innerWidth],
  });

  const yScale = scaleBand({
    domain: data.map((d, index) => `${d.label}-${index}`), // Use index to ensure unique domain values
    range: [0, innerHeight],
    padding: 0.2,
  });

  return (
    <svg
      ref={tooltipContainerRef}
      width={width}
      height={height}
      style={{ display: 'block' }}
    >
      {/* Y-axis labels */}
      <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
        {data.map((datum, index) => {
          const uniqueKey = `${datum.label}-${index}`;
          const y = yScale(uniqueKey);
          if (y === undefined) return null;
          const yPosition = y + yScale.bandwidth() / 2;
          const needsTooltip = datum.label !== datum.fullLabel;

          return (
            <text
              key={`y-label-${index}`}
              x={-10}
              y={yPosition}
              fill={theme.palette.text.secondary}
              fontSize={16}
              textAnchor="end"
              dominantBaseline="middle"
              style={{ userSelect: 'none' }}
              onMouseMove={
                needsTooltip
                  ? createDirectFlexibleTooltipMouseMoveHandler(
                      showTooltip,
                      datum,
                      displayMode,
                      currencyCode
                    )
                  : undefined
              }
              onMouseLeave={() => {
                if (needsTooltip) {
                  hideTooltip();
                }
              }}
            >
              {datum.label}
            </text>
          );
        })}
      </g>

      {/* X-axis for values */}
      {showXAxis && (
        <>
          <AxisBottom
            scale={xScale}
            left={MARGIN.left}
            top={MARGIN.top + innerHeight}
            stroke={theme.palette.divider}
            tickLength={TICK_LENGTH}
            tickStroke={theme.palette.divider}
            tickLabelProps={() => ({
              fill: theme.palette.text.secondary,
              fontSize: 16,
              textAnchor: 'middle',
            })}
            tickFormat={(value) => {
              return displayMode === 'currency'
                ? formatCurrencyAxis(Number(value), currencyCode)
                : formatTimeAxis(Number(value));
            }}
          />

          {/* X-axis line */}
          <line
            x1={MARGIN.left}
            x2={MARGIN.left + innerWidth}
            y1={MARGIN.top + innerHeight}
            y2={MARGIN.top + innerHeight}
            stroke={theme.palette.divider}
            strokeWidth={1}
          />
        </>
      )}

      {/* Vertical grid lines extending through the chart */}
      <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
        {xScale.ticks().map((tickValue) => {
          const x = xScale(tickValue);
          return (
            <line
              key={`grid-${tickValue}`}
              x1={x}
              x2={x}
              y1={0}
              y2={innerHeight}
              stroke={theme.palette.text.secondary}
              strokeWidth={1}
              opacity={0.2}
            />
          );
        })}
      </g>

      {/* Horizontal Bars */}
      <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
        {data.map((datum, index) => {
          const uniqueKey = `${datum.label}-${index}`;
          const barY = yScale(uniqueKey) ?? 0;
          const barHeight = yScale.bandwidth();
          const barWidth = xScale(datum.value);

          return (
            <g key={`bar-${index}`}>
              <rect
                x={0}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={theme.palette.primary.main}
                data-datum={createDataDatumAttribute(datum)}
                onMouseMove={handleMouseMove}
                onMouseLeave={hideTooltip}
                style={{ cursor: 'pointer' }}
              />
              <Text
                x={barWidth + 8 + 80 > innerWidth ? barWidth - 8 : barWidth + 8} // Only move inside if outside label would exceed chart bounds
                y={barY + barHeight / 2}
                verticalAnchor="middle"
                textAnchor={barWidth + 8 + 80 > innerWidth ? 'end' : 'start'} // Adjust text anchor based on position
                fill={
                  barWidth + 8 + 80 > innerWidth
                    ? theme.palette.background.paper
                    : theme.palette.text.primary
                } // Light text inside, dark text outside
                fontSize={16}
                fontWeight="bold"
                style={{ cursor: 'pointer' }}
                onMouseMove={createDirectFlexibleTooltipMouseMoveHandler(
                  showTooltip,
                  datum,
                  displayMode,
                  currencyCode
                )}
                onMouseLeave={hideTooltip}
              >
                {displayMode === 'currency'
                  ? formatCurrencyAbbreviated(datum.value, currencyCode)
                  : formatTimeAbbreviated(datum.value)}
              </Text>
            </g>
          );
        })}
      </g>

      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ p: 1, minWidth: 160 }}>
            <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }}>
              {tooltipData.label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Count:
              </Typography>
              <Typography variant="body2">
                {formatNumber(tooltipData.count)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total:
              </Typography>
              <Typography variant="body2">
                {tooltipData.formattedValue}
              </Typography>
            </Box>
          </Box>
        </TooltipInPortal>
      )}
    </svg>
  );
}

interface StaticXAxisProps {
  data: ChartData[];
  width: number;
  currencyCode?: IsoCurrencyCode;
}

function StaticXAxis({ data, width, currencyCode }: StaticXAxisProps) {
  const theme = useTheme();

  const innerWidth = width - MARGIN.left - MARGIN.right;

  // Calculate appropriate upper bound for the domain (same logic as main chart)
  const maxValue = Math.max(...data.map((d) => d.value), 0);
  const getNiceUpperBound = (value: number): number => {
    if (value === 0) return 100;

    // Find the NEXT power of 10 above the value, then go one level down
    const nextPowerOfTen = Math.pow(10, Math.ceil(Math.log10(value)));
    const incrementPowerOfTen = nextPowerOfTen / 10;

    // Round up to the next increment of that power of 10
    const result = Math.ceil(value / incrementPowerOfTen) * incrementPowerOfTen;
    return result;
  };

  const xScale = scaleLinear({
    domain: [0, getNiceUpperBound(maxValue)],
    range: [0, innerWidth],
  });

  return (
    <>
      <AxisBottom
        scale={xScale}
        left={MARGIN.left}
        top={0}
        stroke={theme.palette.divider}
        tickLength={TICK_LENGTH}
        tickStroke={theme.palette.divider}
        numTicks={8}
        tickValues={(() => {
          const upperBound = getNiceUpperBound(maxValue);
          const ticks = xScale.ticks(8).filter((tick) => tick <= upperBound);
          return ticks;
        })()}
        tickLabelProps={() => ({
          fill: theme.palette.text.secondary,
          fontSize: 16,
          textAnchor: 'middle',
        })}
        tickFormat={(value) => formatCurrencyAxis(Number(value), currencyCode)}
      />

      {/* X-axis line */}
      <line
        x1={MARGIN.left}
        x2={MARGIN.left + innerWidth}
        y1={0}
        y2={0}
        stroke={theme.palette.divider}
        strokeWidth={1}
      />
    </>
  );
}
